import React, { FC, useState } from 'react'
import {
  EmptyResults,
  HeaderTips,
  Navbar,
  RecomendedList,
  SearchInput,
} from '@/education/help/components'
import { Box, Container, VStack } from '@chakra-ui/react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  useGetAllCategoriesQuery,
  useGetArticlesByGroupNamesQuery,
  useSearchArticlesQuery,
} from '@/education/help/services/help-center-query.service'
import { ArticlesContext } from '@/education/help/context/ArticlesContext'
import { buildHelpUri } from '@/education/help/utilities/buildUri'
import { getSameSectionArticles } from '@/education/help/utilities/getSameSectionArticles'
import { HELP_URI } from '@/education/help/domain/constants/Help-uri'

import type {
  Article,
  Category,
} from '@/education/help/services/help-center-query.service'

const calculateHeight = {
  md: 'calc(100vh - 120px)',
  xl: 'calc(100vh - 160px)',
}

type Props = {
  children: React.ReactNode
}

type Acc = {
  [x: string]: Article[]
}

type Group = {
  name: string
  articles: Article[]
}

const HelpCenterLayout: FC<Props> = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { articleSlug } = useParams()

  const [searchTerm, setSearchTerm] = useState('')

  const { data: categoriesData, isLoading: isLoadingCategories } =
    useGetAllCategoriesQuery()
  const { data: groupedArticles, isLoading: isLoadingArticles } =
    useGetArticlesByGroupNamesQuery({
      groupNames: ['recommended', 'tips'],
    })
  const { data: searchResults, isFetching: isLoadingSearch } =
    useSearchArticlesQuery({ searchTerm }, { skip: !searchTerm })

  const { helpArticles: searchedArticles } = searchResults || {}
  const { helpCategories } = categoriesData || {}

  const articlesByGroup =
    !isLoadingArticles && groupedArticles?.helpGroups
      ? groupedArticles.helpGroups.reduce((acc: Acc, group: Group) => {
          acc[group.name] = group.articles
          return acc
        }, {} as Acc)
      : {}

  const articlesFromCategories =
    helpCategories?.flatMap((category: Category) => category.articles) || []

  const allArticles = [
    ...articlesFromCategories,
    ...(articlesByGroup?.recommended || []),
    ...(articlesByGroup?.tips || []),
    ...(searchedArticles ? searchedArticles : []),
  ]

  const uniqueIds = new Set()
  const uniqueArticles = allArticles.filter(article => {
    if (!uniqueIds.has(article.id)) {
      uniqueIds.add(article.id)
      return true
    }
    return false
  })

  const validRecommendedArticles = articlesByGroup?.recommended || []
  const validArticleTips = articlesByGroup?.tips?.[0] || null
  const hasNotArticleUri = !/article/.test(location.pathname)

  const onlyHomeUri = !/category|article/.test(location.pathname)

  const sameSectionArticles = getSameSectionArticles(
    helpCategories || [],
    articleSlug || '',
  )
  const isSameSection =
    location.pathname.includes('article') &&
    articleSlug &&
    !!sameSectionArticles.length

  const validListTitle = !isSameSection
    ? 'Más recomendados'
    : 'Otros de la misma sección'
  const validListArticles = isSameSection
    ? sameSectionArticles
    : validRecommendedArticles

  const noCategories = !searchTerm && helpCategories && !helpCategories?.length

  const contextValues = {
    allArticles: uniqueArticles,
    categories: {
      isLoading: isLoadingCategories,
      data: helpCategories || null,
    },
    searched: {
      isLoading: isLoadingSearch,
      data: searchedArticles || null,
      term: searchTerm,
    },
  }

  const handleOnSearch = (value: string) => {
    if (!value) {
      setSearchTerm('')
      return navigate(HELP_URI)
    }

    setSearchTerm(value)
    navigate(`${HELP_URI}/?busqueda=${value}`)
  }

  const handleNavigateToArticle = (article: string) => {
    if (!article) return null

    const articleUri = buildHelpUri(article)['article']
    return navigate(articleUri)
  }

  return (
    <ArticlesContext.Provider value={contextValues}>
      <Container
        pl={{ md: 10, xl: 100, lg: 50 }}
        maxWidth={'1920px'}
        flex={1}
        pr={0}
        height={calculateHeight}
      >
        <Navbar />
        <Box display={'flex'} gap={6}>
          <VStack
            display={'flex'}
            flexDirection={'column'}
            width={'100%'}
            gap={6}
            pb={20}
          >
            {hasNotArticleUri && (
              <HeaderTips
                isLoading={isLoadingArticles}
                title={validArticleTips?.title || ''}
                description={validArticleTips?.description || ''}
                slug={validArticleTips?.slug || ''}
                labelButton={'Explorar Artículo'}
                onClick={handleNavigateToArticle}
              />
            )}
            <SearchInput
              onSearch={handleOnSearch}
              placeholder='¿Cómo podemos ayudarte?'
            />
            {onlyHomeUri && (
              <EmptyResults
                text={'Aún no hay categorías disponibles 🤷'}
                isShowed={!!noCategories}
              />
            )}

            {children}
          </VStack>
          <RecomendedList
            title={validListTitle}
            articles={validListArticles}
            isLoading={isLoadingArticles}
            height={calculateHeight}
            onClick={handleNavigateToArticle}
          />
        </Box>
      </Container>
    </ArticlesContext.Provider>
  )
}

export const HelpCenterRoot = () => {
  return (
    <HelpCenterLayout>
      <Outlet />
    </HelpCenterLayout>
  )
}
